import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Collapse from '@material-ui/core/Collapse';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { makeStyles } from '@material-ui/core/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { LeanIXFieldValue } from './LeanIXAppId';

/**
 * CreateFactSheetPanel
 *
 * Inline "I don't have an App ID yet" helper rendered by the `LeanIXAppId`
 * field. It COLLECTS the details needed to create a LeanIX Application fact
 * sheet (name, owning org unit, IT custodian, AWS hosting) but does NOT create
 * anything itself. The values are written straight back into the field's object
 * value via `onChange`; Repo Foundry creates the fact sheet at execution time
 * (Review -> Create), so abandoning the wizard never leaves an orphaned App ID.
 *
 * It is shown ONLY when the App ID is required for the current selection
 * (production `service`/`website`, non-sandbox) AND no existing App ID has been
 * typed. Typing an App ID hides it and the normal flow continues unchanged.
 *
 * Data flow (all via the PDP backend `repo-foundry` proxy — never LeanIX
 * directly from the browser):
 *   GET /api/repo-foundry/org-units      -> { orgUnits: [{ id, name, displayName }] }
 *   GET /api/repo-foundry/leanix-members -> { members: [{ email, name, userId }] }
 */

type OrgUnit = { id: string; name?: string; displayName?: string };

type UserOption = { email: string; displayName: string; ref: string };

type LeanIXMember = { email?: string; name?: string; userId?: string };

// Conflicting fact sheet returned by the name check when the name is taken.
type NameMatch = {
  id: string;
  name: string;
  appId: string | null;
  factSheetUrl: string;
};

const useStyles = makeStyles(theme => ({
  panel: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
  // Clickable disclosure header — the only thing shown until the user expands.
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  expandIcon: {
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
  },
  field: {
    marginTop: theme.spacing(1.5),
  },
  aws: {
    marginTop: theme.spacing(3),
  },
}));

const orgUnitLabel = (u: OrgUnit): string => u.displayName ?? u.name ?? u.id;

export const CreateFactSheetPanel = ({
  value,
  onChange,
}: {
  value: LeanIXFieldValue;
  onChange: (patch: Partial<LeanIXFieldValue>) => void;
}) => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);

  // Manual disclosure toggle. Effective expansion (`expanded`, below) also
  // stays open whenever any field is filled, so the panel survives Back/Next
  // remounts and only collapses when every field is empty.
  const [userExpanded, setUserExpanded] = useState(false);

  const [custodianOptions, setCustodianOptions] = useState<UserOption[]>([]);
  const [loadingCustodians, setLoadingCustodians] = useState(false);
  const custodianSearchTimer = useRef<ReturnType<typeof setTimeout>>();
  // Monotonic id of the most recent custodian request, so a slower response for
  // an older query can't overwrite the results of a newer one.
  const custodianReqId = useRef(0);

  const [orgUnits, setOrgUnits] = useState<OrgUnit[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [unitsError, setUnitsError] = useState<string | undefined>();

  // Accountable EA resolved server-side for the selected org unit (matrix +
  // best-effort name lookup). Shown as a hint so the user knows who will be
  // assigned before the fact sheet is created.
  const [ea, setEa] = useState<{ name?: string; email?: string } | null>(null);
  const [eaLoading, setEaLoading] = useState(false);

  // Live LeanIX duplicate-name guard. LeanIX enforces uniqueness on
  // (display name, Application), so a taken name would fail creation at submit;
  // this warns the user as they type. Submit-time validation blocks Next.
  const [nameStatus, setNameStatus] = useState<
    'idle' | 'checking' | 'available' | 'taken' | 'error'
  >('idle');
  // The conflicting fact sheet (id, App ID, link) when the name is taken, so the
  // user can adopt it instead of creating a duplicate.
  const [nameMatch, setNameMatch] = useState<NameMatch | null>(null);
  const nameCheckTimer = useRef<ReturnType<typeof setTimeout>>();

  // The field's object value is the single source of truth — this panel holds
  // no draft state of its own for the collected fields.
  const name = value.name ?? '';
  const custodianEmail = value.custodianEmail ?? '';
  // Tri-state: undefined until the user picks Yes/No (no silent default).
  const hostedOnAws = value.hostedOnAws;
  let awsRadioValue = '';
  if (hostedOnAws === true) {
    awsRadioValue = 'yes';
  } else if (hostedOnAws === false) {
    awsRadioValue = 'no';
  }
  // LeanIX Application category; blank until the user chooses a subtype.
  const subtype = value.subtype ?? '';
  const orgUnit = orgUnits.find(u => u.id === (value.orgUnitId ?? '')) ?? null;

  // Keep the panel open while anything is entered; only an entirely empty panel
  // may be collapsed. This also makes it survive Back/Next, which remounts the
  // field and would otherwise reset a purely local expanded flag.
  const hasAnyValue = Boolean(
    name.trim() ||
      custodianEmail.trim() ||
      (value.orgUnitId ?? '') ||
      hostedOnAws !== undefined,
  );
  const expanded = userExpanded || hasAnyValue;

  // Load the org-unit options once.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoadingUnits(true);
      setUnitsError(undefined);
      try {
        const baseUrl = configApi.getString('backend.baseUrl');
        const res = await fetchApi.fetch(
          `${baseUrl}/api/repo-foundry/org-units`,
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error ?? `Request failed (${res.status}).`);
        }
        const units: OrgUnit[] = Array.isArray(data?.orgUnits)
          ? data.orgUnits
          : [];
        if (active) setOrgUnits(units);
      } catch (e: any) {
        if (active) setUnitsError(e?.message ?? 'Could not load org units.');
      } finally {
        if (active) setLoadingUnits(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [configApi, fetchApi]);

  // Debounced type-ahead: query LeanIX workspace members server-side (via the
  // repo-foundry proxy) as the user types. Restricting to real LeanIX members
  // guarantees a valid RESPONSIBLE subscription email. Fires after 2+ chars and
  // coalesces keystrokes.
  const searchCustodians = useCallback(
    (term: string) => {
      if (custodianSearchTimer.current) {
        clearTimeout(custodianSearchTimer.current);
      }
      const query = term.trim();
      if (query.length < 2) {
        setCustodianOptions([]);
        setLoadingCustodians(false);
        return;
      }
      setLoadingCustodians(true);
      custodianSearchTimer.current = setTimeout(async () => {
        const requestId = ++custodianReqId.current;
        try {
          const baseUrl = configApi.getString('backend.baseUrl');
          const res = await fetchApi.fetch(
            `${baseUrl}/api/repo-foundry/leanix-members?q=${encodeURIComponent(
              query,
            )}`,
          );
          const data = await res.json();
          if (!res.ok) {
            throw new Error(data?.error ?? `Request failed (${res.status}).`);
          }
          const members: LeanIXMember[] = Array.isArray(data?.members)
            ? data.members
            : [];
          const options = members
            .map(m => ({
              email: m.email ?? '',
              displayName: m.name || (m.email ?? ''),
              ref: m.userId ?? '',
            }))
            .filter(option => option.email);
          // Only apply if this is still the latest request (drop stale responses).
          if (custodianReqId.current === requestId) {
            setCustodianOptions(options);
          }
        } catch {
          if (custodianReqId.current === requestId) {
            setCustodianOptions([]);
          }
        } finally {
          if (custodianReqId.current === requestId) {
            setLoadingCustodians(false);
          }
        }
      }, 300);
    },
    [configApi, fetchApi],
  );

  // Clear any pending debounce on unmount.
  useEffect(
    () => () => {
      if (custodianSearchTimer.current) {
        clearTimeout(custodianSearchTimer.current);
      }
    },
    [],
  );

  // Debounced fact-sheet name availability check against LeanIX (via the
  // repo-foundry proxy). Fires ~500ms after the user stops typing; an empty
  // name resets to idle and a check failure is treated as non-blocking here
  // (submit-time validation re-checks and is the hard gate).
  useEffect(() => {
    if (nameCheckTimer.current) {
      clearTimeout(nameCheckTimer.current);
    }
    const q = name.trim();
    if (!q) {
      setNameStatus('idle');
      setNameMatch(null);
      return undefined;
    }
    setNameStatus('checking');
    let active = true;
    nameCheckTimer.current = setTimeout(async () => {
      try {
        const baseUrl = configApi.getString('backend.baseUrl');
        const res = await fetchApi.fetch(
          `${baseUrl}/api/repo-foundry/leanix-name-check?name=${encodeURIComponent(
            q,
          )}`,
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error ?? `Request failed (${res.status}).`);
        }
        if (active) {
          if (data?.available === false) {
            setNameStatus('taken');
            setNameMatch(data?.match ?? null);
          } else {
            setNameStatus('available');
            setNameMatch(null);
          }
        }
      } catch {
        if (active) setNameStatus('error');
      }
    }, 500);
    return () => {
      active = false;
      if (nameCheckTimer.current) {
        clearTimeout(nameCheckTimer.current);
      }
    };
  }, [name, configApi, fetchApi]);

  // Resolve the accountable EA whenever the selected org unit changes.
  useEffect(() => {
    const orgUnitId = (value.orgUnitId ?? '').trim();
    if (!orgUnitId) {
      setEa(null);
      setEaLoading(false);
      return undefined;
    }
    let active = true;
    setEaLoading(true);
    (async () => {
      try {
        const baseUrl = configApi.getString('backend.baseUrl');
        const res = await fetchApi.fetch(
          `${baseUrl}/api/repo-foundry/resolve-ea?orgUnitId=${encodeURIComponent(
            orgUnitId,
          )}`,
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error ?? `Request failed (${res.status}).`);
        }
        if (active) setEa({ name: data?.name, email: data?.email });
      } catch {
        if (active) setEa(null);
      } finally {
        if (active) setEaLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [value.orgUnitId, configApi, fetchApi]);

  const toggleExpanded = () => setUserExpanded(prev => !prev);

  // Helper text under the org unit picker: keep the original guidance until an
  // org unit is selected, then surface the resolved accountable EA.
  const eaLabel = ea?.name || ea?.email;
  const orgUnitSelected = Boolean((value.orgUnitId ?? '').trim());
  const defaultOrgUnitHelperText =
    'The organizational unit that owns this application.';
  let orgUnitHelperText: string = defaultOrgUnitHelperText;
  if (unitsError) {
    orgUnitHelperText = unitsError;
  } else if (!orgUnitSelected) {
    orgUnitHelperText = defaultOrgUnitHelperText;
  } else if (eaLoading) {
    orgUnitHelperText = 'Resolving accountable Enterprise Architect…';
  } else if (eaLabel) {
    orgUnitHelperText = `${eaLabel} will be assigned as the accountable Enterprise Architect.`;
  }

  let nameHelperText: ReactNode =
    'This becomes the LeanIX fact sheet name — enter the real application name.';
  if (nameStatus === 'checking') {
    nameHelperText = 'Checking name availability…';
  } else if (nameStatus === 'taken') {
    if (nameMatch) {
      nameHelperText = (
        <>
          Existing fact sheet:{' '}
          <Link
            href={nameMatch.factSheetUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {nameMatch.name}
          </Link>
          {nameMatch.appId ? ` — ${nameMatch.appId}` : ''}
        </>
      );
    } else {
      nameHelperText = `A LeanIX fact sheet named “${name.trim()}” already exists — please use a different name.`;
    }
  }

  return (
    <Box className={classes.panel}>
      <Box
        className={classes.header}
        onClick={toggleExpanded}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <Typography variant="subtitle2">
          Don&apos;t have an App ID? Create a LeanIX fact sheet
        </Typography>
        <IconButton
          size="small"
          aria-label={
            expanded ? 'Hide fact sheet fields' : 'Show fact sheet fields'
          }
          className={`${classes.expandIcon} ${
            expanded ? classes.expandIconOpen : ''
          }`}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Typography variant="body2" color="textSecondary">
          Production services and websites need a LeanIX Application. Fill these
          in and the fact sheet is created for you when the repository is
          created — its App ID is filled in automatically.
        </Typography>

        <TextField
          className={classes.field}
          fullWidth
          required
          label="Application / Fact sheet name"
          placeholder="Enter the application name"
          error={nameStatus === 'taken'}
          helperText={nameHelperText}
          value={name}
          onChange={e => onChange({ name: e.target.value })}
          InputProps={{
            endAdornment:
              nameStatus === 'checking' ? (
                <CircularProgress size={18} />
              ) : undefined,
          }}
        />

        {nameStatus === 'taken' && nameMatch?.appId && (
          <Box className={classes.field}>
            <Button
              size="small"
              color="primary"
              variant="outlined"
              onClick={() =>
                onChange({
                  appId: nameMatch.appId ?? undefined,
                  name: undefined,
                  orgUnitId: undefined,
                  custodianEmail: undefined,
                  hostedOnAws: undefined,
                  subtype: undefined,
                })
              }
            >
              Use this fact sheet
            </Button>
          </Box>
        )}

        <TextField
          className={classes.field}
          fullWidth
          select
          required
          label="Application subtype"
          helperText="The LeanIX Application category for this fact sheet."
          value={subtype}
          onChange={e => onChange({ subtype: e.target.value })}
        >
          <MenuItem value="businessApplication">Business Application</MenuItem>
          <MenuItem value="microservice">Application Module</MenuItem>
        </TextField>

        <Autocomplete<OrgUnit>
          className={classes.field}
          options={orgUnits}
          loading={loadingUnits}
          value={orgUnit}
          getOptionLabel={orgUnitLabel}
          getOptionSelected={(a, b) => a.id === b.id}
          onChange={(_e, selected) =>
            onChange({ orgUnitId: selected?.id ?? '' })
          }
          renderInput={params => (
            <TextField
              {...params}
              required
              label="Owning org unit"
              helperText={orgUnitHelperText}
              error={Boolean(unitsError)}
            />
          )}
        />

        <Autocomplete<UserOption, false, false, true>
          className={classes.field}
          freeSolo
          options={custodianOptions}
          loading={loadingCustodians}
          filterOptions={x => x}
          inputValue={custodianEmail}
          getOptionLabel={option =>
            typeof option === 'string' ? option : option.email
          }
          getOptionSelected={(a, b) => a.email === b.email}
          onInputChange={(_e, input, reason) => {
            // Ignore the mount-time 'reset' MUI fires with an empty input: it
            // would mark the field touched and surface the required error before
            // the user has interacted. Only real typing / clearing propagates.
            if (reason === 'input' || reason === 'clear') {
              onChange({ custodianEmail: input });
            }
            if (reason === 'input') searchCustodians(input);
          }}
          onChange={(_e, selected) => {
            if (selected && typeof selected !== 'string') {
              onChange({ custodianEmail: selected.email });
            }
          }}
          renderOption={option => (
            <Box>
              <Typography variant="body2">{option.displayName}</Typography>
              <Typography variant="caption" color="textSecondary">
                {option.email}
              </Typography>
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              required
              label="IT Custodian"
              placeholder="Search by name or email"
              helperText="Search LeanIX members by name or email, or type an email directly (RESPONSIBLE subscription)."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingCustodians ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <FormControl component="fieldset" required className={classes.aws}>
          <FormLabel component="legend">
            Is the application hosted on AWS?
          </FormLabel>
          <RadioGroup
            row
            value={awsRadioValue}
            onChange={e => onChange({ hostedOnAws: e.target.value === 'yes' })}
          >
            <FormControlLabel
              value="yes"
              control={<Radio color="primary" size="small" />}
              label={<Typography variant="body2">Yes</Typography>}
            />
            <FormControlLabel
              value="no"
              control={<Radio color="primary" size="small" />}
              label={<Typography variant="body2">No</Typography>}
            />
          </RadioGroup>
        </FormControl>
      </Collapse>
    </Box>
  );
};
